import { browser_local_storage } from './browser-storage'

export interface AppState {
  is_dark_mode: boolean
  cnpj: string | null
  phone: string | null
  station_name: string | null
  station_location: string | null
  station_logo: string | null
  latitude: string | null
  longitude: string | null
  whatsapp_link: string | null
  voucher: Record<string, unknown> | null
  auth_token: string | null
  client_auth_token: string | null
  driver_phone: string | null
  driver_auth_token: string | null
  driver_tipo_motorista: number | null
  driver_placa: string | null
  driver_autorizacao: string | null
  driver_veiculo: string | null
  driver_offer_product_code: string | null
  driver_offer_payment_id: string | null
  driver_voucher: Record<string, unknown> | null
  driver_ev_navigation: Record<string, unknown> | null
  rede_mode: 'default' | 'rede' | 'unconfigured'
  rede_host: string | null
  rede_uuid: string | null
  rede_nome: string | null
  rede_logo: string | null
  rede_telefone: string | null
  rede_email: string | null
}

const PERSIST_KEYS: (keyof AppState)[] = [
  'cnpj', 'phone', 'station_name', 'station_location', 'station_logo',
  'latitude', 'longitude', 'whatsapp_link', 'voucher', 'auth_token',
  'client_auth_token', 'driver_phone', 'driver_auth_token', 'driver_tipo_motorista', 'driver_placa', 'driver_autorizacao', 'driver_veiculo',
  'driver_offer_product_code', 'driver_offer_payment_id', 'driver_voucher',
  'driver_ev_navigation',
]
const PREFIX = 'fidelidade_'

type Listener = (value: unknown, old_value: unknown) => void

class Store {
  private state: AppState
  private listeners: Map<string, Set<Listener>> = new Map()

  constructor() {
    this.state = {
      is_dark_mode: false,
      cnpj: null,
      phone: null,
      station_name: null,
      station_location: null,
      station_logo: null,
      latitude: null,
      longitude: null,
      whatsapp_link: null,
      voucher: null,
      auth_token: null,
      client_auth_token: null,
      driver_phone: null,
      driver_auth_token: null,
      driver_tipo_motorista: null,
      driver_placa: null,
      driver_autorizacao: null,
      driver_veiculo: null,
      driver_offer_product_code: null,
      driver_offer_payment_id: null,
      driver_voucher: null,
      driver_ev_navigation: null,
      rede_mode: 'default',
      rede_host: null,
      rede_uuid: null,
      rede_nome: null,
      rede_logo: null,
      rede_telefone: null,
      rede_email: null,
    }
    for (const key of PERSIST_KEYS) {
      const raw = browser_local_storage.getItem(PREFIX + key)
      if (raw !== null) {
        try {
          ;(this.state as unknown as Record<string, unknown>)[key] = JSON.parse(raw) as unknown
        } catch {
          ;(this.state as unknown as Record<string, unknown>)[key] = raw
        }
      }
    }
  }

  get<K extends keyof AppState>(key: K): AppState[K] {
    return this.state[key]
  }

  set<K extends keyof AppState>(key: K, value: AppState[K]): void {
    const old = this.state[key]
    this.state[key] = value
    if ((PERSIST_KEYS as string[]).includes(key)) {
      if (value === null || value === undefined) {
        browser_local_storage.removeItem(PREFIX + key)
      } else {
        browser_local_storage.setItem(PREFIX + key, JSON.stringify(value))
      }
    }
    this.emit(key, value, old)
  }

  on<K extends keyof AppState>(key: K, callback: (value: AppState[K], old_value: AppState[K]) => void): () => void {
    const listener = callback as Listener
    let set = this.listeners.get(key)
    if (!set) {
      set = new Set()
      this.listeners.set(key, set)
    }
    set.add(listener)
    return () => set.delete(listener)
  }

  emit<K extends keyof AppState>(key: K, value: AppState[K], old_value: AppState[K]): void {
    const set = this.listeners.get(key)
    if (set) set.forEach(fn => fn(value, old_value))
  }

  subscribe(callback: (value: AppState, old_value: AppState) => void): () => void {
    const unsubs: (() => void)[] = []
    for (const key of Object.keys(this.state) as (keyof AppState)[]) {
      unsubs.push(this.on(key, () => callback(this.state, this.state)))
    }
    return () => unsubs.forEach(fn => fn())
  }

  get_state(): Readonly<AppState> {
    return this.state
  }
}

export const store = new Store()
export default store
